/**
 * @fileoverview Modal component for creating and editing 'Rasca y gana' (Scratcher) themes.
 * Features 5 tabs, Drag & Drop file uploads with SkiaSharp optimization,
 * live multi-view preview, coin size brush selector, N-player support, and full sanitization.
 * 
 * @module components/Admin/Games/Scratcher/ScratcherThemeModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { UploadMedia, SaveTheme } from './../../../../utilities/BL/Scratcher';

/**
 * Available typography families for Scratcher themes.
 */
const AVAILABLE_FONTS = [
    { value: 'Bangers', label: 'Bangers (Comic / Dinámica)' },
    { value: 'Fredoka', label: 'Fredoka (Moderna / Redonda)' },
    { value: 'Cinzel', label: 'Cinzel (Épica / Elegante)' },
    { value: 'Montserrat', label: 'Montserrat (Limpia / Sans-Serif)' },
    { value: 'Orbitron', label: 'Orbitron (Futurista / Digital)' },
    { value: 'Russo One', label: 'Russo One (Deportiva / Imponente)' },
    { value: 'Impact', label: 'Impact (Clásica Arcade)' }
];

/**
 * Reusable Drag & Drop Field component for individual media assets.
 * 
 * @function DragAndDropField
 * @param {Object} props - Component properties.
 * @param {string} props.fieldLabel - Text label displayed above the drop area.
 * @param {string} props.targetFieldName - Form property key being updated.
 * @param {string} props.mediaCategory - Category identifier for SkiaSharp optimization.
 * @param {string} props.currentValue - Current relative or absolute URL of the asset.
 * @param {Function} props.onUploadFile - Handler triggered when a file is dropped or selected.
 * @param {Function} props.resolveMediaUrl - Helper that resolves absolute URLs for preview.
 * @param {boolean} [props.isProcessing=false] - Whether an upload is in progress.
 * @param {string} [props.helpText] - Optional helper text below the dropzone.
 * @param {string} [props.acceptTypes='image/*,video/mp4'] - Accepted mime types.
 * @param {Function} [props.onClear] - Optional callback to clear the field.
 */
const DragAndDropField = ({
    fieldLabel,
    targetFieldName,
    mediaCategory,
    currentValue,
    onUploadFile,
    resolveMediaUrl,
    isProcessing = false,
    helpText = '',
    acceptTypes = 'image/*,video/mp4',
    onClear = null
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = dropEvent.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFile = changeEvent.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    return (
        <div className="mb-3">
            {fieldLabel && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-bold text-dark mb-0 small">
                        {fieldLabel}
                    </label>
                    {currentValue && onClear && (
                        <button
                            type="button"
                            className="btn btn-link text-danger p-0 small text-decoration-none"
                            onClick={() => onClear(targetFieldName)}
                            title="Quitar archivo"
                        >
                            <i className="bi bi-trash me-1"></i>Quitar
                        </button>
                    )}
                </div>
            )}
            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isDragHoverActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetFieldName}`).click()}
                style={{ minHeight: '85px', cursor: 'pointer' }}
            >
                {isProcessing ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo y procesando...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-primary fs-4 d-block mb-1"></i>
                        <span className="small text-muted d-block">
                            Arrastra aquí tu archivo o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                        {helpText && <small className="text-secondary d-block mt-1">{helpText}</small>}
                    </>
                )}
                <input
                    id={`file-input-${targetFieldName}`}
                    type="file"
                    className="d-none"
                    accept={acceptTypes}
                    onChange={handleNativeFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    {currentValue.toLowerCase().endsWith('.mp4') ? (
                        <div className="badge bg-secondary p-2">
                            <i className="bi bi-film me-1"></i> Video: {currentValue}
                        </div>
                    ) : (
                        <img
                            src={resolveMediaUrl(currentValue)}
                            alt="Vista previa"
                            style={{ maxHeight: '75px', maxWidth: '100%', objectFit: 'contain' }}
                            onError={(errorEvent) => {
                                errorEvent.target.style.display = 'none';
                            }}
                        />
                    )}
                    <div className="small text-truncate text-muted mt-1" style={{ maxWidth: '100%' }}>
                        {currentValue}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Main Scratcher Theme Modal Component (Tile ID 43).
 * 
 * @function ScratcherThemeModal
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {Function} props.onCloseModal - Handler invoked to dismiss modal.
 * @param {Object} [props.initialThemeData=null] - Theme data when editing, or null for creating.
 * @param {Function} props.onThemeSavedSuccessfully - Callback invoked after successful save.
 * @returns {React.ReactElement|null} Modal JSX markup.
 */
export const ScratcherThemeModal = ({
    isOpen,
    onCloseModal,
    initialThemeData = null,
    onThemeSavedSuccessfully
}) => {
    const { showMessage } = useFloatMessage();
    const [activeTabKey, setActiveTabKey] = useState('identity');
    const [previewModeKey, setPreviewModeKey] = useState('cover'); // 'cover', 'stadium', 'scratchCard', 'mobile'
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');

    // Default Theme Template
    const defaultThemeStructure = {
        id: 0,
        name: '',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        coverTitle: 'RASCA Y GANA',
        coverPresentsText: 'PRESENTA',
        showBrandLogo: true,
        showSponsorLogo: true,
        showCoverTitle: true,
        showPresentsText: true,
        darkness: 45,

        // Portada
        coverBackgroundType: 'image',
        coverBackgroundColor: '#0a0a14',
        coverBackgroundUrl: '',
        coverTextColor: '#ffffff',

        // Escenario Estadio
        gameBackgroundType: 'image',
        gameBackgroundColor: '#121224',
        gameBackgroundUrl: '',
        gameTextColor: '#ffffff',
        fontFamily: 'Bangers',

        // Capa de Raspado y Premio
        scratchCoverImageUrl: '',
        scratchHiddenImageUrl: '',
        scratchCoverColor: '#C0C0C0',
        scratchToolImageUrl: '',
        showScratchTool: false,
        coinSize: 25,

        // Jugadores Principales (J1 y J2)
        player1Name: 'Jugador 1',
        player1Avatar: '',
        player1Winner: '',
        player1Color: '#00ffcc',

        player2Name: 'Jugador 2',
        player2Avatar: '',
        player2Winner: '',
        player2Color: '#ff0055',

        // Móvil Aficionado
        fanBackgroundType: 'color',
        fanBackgroundColor: '#0d0e1a',
        fanBackgroundUrl: '',
        fanTextColor: '#ffffff',

        // Parámetros de Partida
        targetPercentage: 100.0,
        maxTimeSeconds: 20,

        // Jugadores Extra
        extraPlayers: []
    };

    const [themeFormState, setThemeFormState] = useState(defaultThemeStructure);

    useEffect(() => {
        if (initialThemeData && initialThemeData.id > 0) {
            setThemeFormState({
                ...defaultThemeStructure,
                ...initialThemeData,
                extraPlayers: Array.isArray(initialThemeData.extraPlayers)
                    ? [...initialThemeData.extraPlayers]
                    : []
            });
        } else {
            setThemeFormState(defaultThemeStructure);
        }
        setActiveTabKey('identity');
        setPreviewModeKey('cover');
    }, [initialThemeData, isOpen]);

    if (!isOpen) return null;

    /**
     * Resolves absolute media URLs for rendering previews.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Updates an individual scalar property on the theme form state.
     */
    const handleFieldChange = (fieldName, fieldValue) => {
        setThemeFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    /**
     * Handles single media upload through the backend controller.
     */
    const handleSingleFileUpload = async (fileObject, mediaCategory, targetFieldName) => {
        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando archivo...' });

        try {
            const uploadResponse = await UploadMedia(fileObject, mediaCategory);
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Archivo cargado con éxito.' });
                handleFieldChange(targetFieldName, uploadResponse.url);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: uploadResponse.message || 'Error al cargar el archivo.' });
            }
        } catch (uploadException) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado durante la carga.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    /**
     * Clears a media field url.
     */
    const handleClearField = (targetFieldName) => {
        handleFieldChange(targetFieldName, '');
    };

    // ==========================================
    // EXTRA PLAYERS MANAGEMENT
    // ==========================================

    const handleAddExtraPlayer = () => {
        const nextIndex = themeFormState.extraPlayers.length + 3;
        const newPlayer = {
            id: `player${nextIndex}`,
            name: `Jugador ${nextIndex}`,
            color: nextIndex % 2 === 1 ? '#ffaa00' : '#aa00ff',
            avatarUrl: '',
            winnerAvatarUrl: '',
            coinSize: themeFormState.coinSize || 25,
            scratchCoverImageUrl: '',
            scratchHiddenImageUrl: ''
        };

        setThemeFormState((previousState) => ({
            ...previousState,
            extraPlayers: [...previousState.extraPlayers, newPlayer]
        }));
    };

    const handleUpdateExtraPlayer = (index, fieldName, fieldValue) => {
        setThemeFormState((previousState) => {
            const updated = [...previousState.extraPlayers];
            updated[index] = {
                ...updated[index],
                [fieldName]: fieldValue
            };
            return {
                ...previousState,
                extraPlayers: updated
            };
        });
    };

    const handleRemoveExtraPlayer = (index) => {
        setThemeFormState((previousState) => {
            const updated = previousState.extraPlayers.filter((_, i) => i !== index);
            return {
                ...previousState,
                extraPlayers: updated
            };
        });
    };

    // ==========================================
    // SAVE THEME
    // ==========================================

    const handleSaveThemeSubmit = async (event) => {
        event.preventDefault();

        if (!themeFormState.name || themeFormState.name.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El nombre del tema es obligatorio.' });
            return;
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando configuración del tema...' });

        try {
            const saveResponse = await SaveTheme(themeFormState);
            if (saveResponse.success && saveResponse.data) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema guardado exitosamente.' });
                if (onThemeSavedSuccessfully) {
                    onThemeSavedSuccessfully(saveResponse.data);
                }
                onCloseModal();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: saveResponse.message || 'Error al guardar el tema.' });
            }
        } catch (saveError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al guardar el tema.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1050 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    
                    {/* Header */}
                    <div className="modal-header bg-dark text-white border-0 py-3 px-4">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-hand-index-thumb-fill text-warning fs-3 me-3"></i>
                            <div>
                                <h5 className="modal-title fw-bold mb-0">
                                    {themeFormState.id > 0 ? `Editar Tema: ${themeFormState.name}` : 'Crear Nuevo Tema de Rasca y Gana'}
                                </h5>
                                <small className="text-secondary">Personaliza la portada, el escenario, la capa de raspado, los avatares y las reglas</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            onClick={onCloseModal}
                            disabled={isSavingInProgress}
                        ></button>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="bg-light border-bottom px-4 pt-2">
                        <ul className="nav nav-tabs border-0 flex-nowrap overflow-auto">
                            <li className="nav-item">
                                <button
                                    className={`nav-link text-nowrap fw-bold ${activeTabKey === 'identity' ? 'active bg-white border-bottom-0 text-primary' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('identity')}
                                    type="button"
                                >
                                    <i className="bi bi-card-heading me-1"></i> 1. Identidad & Portada
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link text-nowrap fw-bold ${activeTabKey === 'stage' ? 'active bg-white border-bottom-0 text-primary' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('stage')}
                                    type="button"
                                >
                                    <i className="bi bi-tv me-1"></i> 2. Escenario Estadio
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link text-nowrap fw-bold ${activeTabKey === 'scratchLayer' ? 'active bg-white border-bottom-0 text-primary' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('scratchLayer')}
                                    type="button"
                                >
                                    <i className="bi bi-eraser-fill me-1"></i> 3. Raspado y Premio
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link text-nowrap fw-bold ${activeTabKey === 'players' ? 'active bg-white border-bottom-0 text-primary' : 'text-secondary'}`}
                                    onClick={() => setActiveTabKey('players')}
                                    type="button"
                                >
                                    <i className="bi bi-people-fill me-1"></i> 4. Jugadores & Móvil
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Modal Body: Split Form + Live Preview */}
                    <div className="modal-body p-4 bg-white">
                        <div className="row g-4">
                            
                            {/* Left Column: Form Controls */}
                            <div className="col-lg-7">
                                <form onSubmit={handleSaveThemeSubmit}>
                                    
                                    {/* TAB 1: IDENTIDAD & PORTADA */}
                                    {activeTabKey === 'identity' && (
                                        <div className="animate__animated animate__fadeIn">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-info-circle me-1"></i> Información General y Pantalla de Portada
                                            </h6>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-dark">Nombre del Tema *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Ej: Clásico Potosino, San Luis vs Chivas, Golden Ticket"
                                                    value={themeFormState.name}
                                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                                    required
                                                />
                                            </div>

                                            {/* Título de Portada con Switch */}
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <label className="form-label fw-bold small text-dark mb-0">Título de Portada</label>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="switchShowCoverTitle"
                                                            checked={themeFormState.showCoverTitle !== false}
                                                            onChange={(e) => handleFieldChange('showCoverTitle', e.target.checked)}
                                                        />
                                                        <label className="form-check-label small text-muted ms-1" htmlFor="switchShowCoverTitle">
                                                            {themeFormState.showCoverTitle !== false ? 'Mostrar' : 'Ocultar'}
                                                        </label>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="RASCA Y GANA"
                                                    value={themeFormState.coverTitle || ''}
                                                    onChange={(e) => handleFieldChange('coverTitle', e.target.value)}
                                                    disabled={themeFormState.showCoverTitle === false}
                                                />
                                            </div>

                                            {/* Texto de Presentación "PRESENTA" con Switch */}
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <label className="form-label fw-bold small text-dark mb-0">Texto de Presentación</label>
                                                    <div className="form-check form-switch mb-0">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="switchShowPresentsText"
                                                            checked={themeFormState.showPresentsText !== false}
                                                            onChange={(e) => handleFieldChange('showPresentsText', e.target.checked)}
                                                        />
                                                        <label className="form-check-label small text-muted ms-1" htmlFor="switchShowPresentsText">
                                                            {themeFormState.showPresentsText !== false ? 'Mostrar' : 'Ocultar'}
                                                        </label>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="PRESENTA"
                                                    value={themeFormState.coverPresentsText || ''}
                                                    onChange={(e) => handleFieldChange('coverPresentsText', e.target.value)}
                                                    disabled={themeFormState.showPresentsText === false}
                                                />
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="small fw-bold text-dark">Logotipo de Marca / Club</span>
                                                        <div className="form-check form-switch mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                id="switchShowBrandLogo"
                                                                checked={themeFormState.showBrandLogo !== false}
                                                                onChange={(e) => handleFieldChange('showBrandLogo', e.target.checked)}
                                                            />
                                                            <label className="form-check-label small text-muted ms-1" htmlFor="switchShowBrandLogo">
                                                                {themeFormState.showBrandLogo !== false ? 'Mostrar' : 'Ocultar'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <DragAndDropField
                                                        fieldLabel=""
                                                        targetFieldName="brandLogoUrl"
                                                        mediaCategory="logo"
                                                        currentValue={themeFormState.brandLogoUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'brandLogoUrl'}
                                                        onClear={handleClearField}
                                                        helpText="PNG transparente (500x500)"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="small fw-bold text-dark">Logotipo de Patrocinador</span>
                                                        <div className="form-check form-switch mb-0">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                id="switchShowSponsorLogo"
                                                                checked={themeFormState.showSponsorLogo !== false}
                                                                onChange={(e) => handleFieldChange('showSponsorLogo', e.target.checked)}
                                                            />
                                                            <label className="form-check-label small text-muted ms-1" htmlFor="switchShowSponsorLogo">
                                                                {themeFormState.showSponsorLogo !== false ? 'Mostrar' : 'Ocultar'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <DragAndDropField
                                                        fieldLabel=""
                                                        targetFieldName="sponsorLogoUrl"
                                                        mediaCategory="logo"
                                                        currentValue={themeFormState.sponsorLogoUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'sponsorLogoUrl'}
                                                        onClear={handleClearField}
                                                        helpText="PNG transparente (500x500)"
                                                    />
                                                </div>
                                            </div>

                                            <hr className="my-3" />
                                            <h6 className="fw-bold text-secondary mb-3">Fondo de Portada y Oscurecimiento</h6>

                                            <div className="row g-3 mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo</label>
                                                    <select
                                                        className="form-select"
                                                        value={themeFormState.coverBackgroundType || 'image'}
                                                        onChange={(e) => handleFieldChange('coverBackgroundType', e.target.value)}
                                                    >
                                                        <option value="image">Imagen estática</option>
                                                        <option value="video">Video animado (MP4)</option>
                                                        <option value="color">Color sólido</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Base</label>
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color me-2"
                                                            value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                            onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase"
                                                            value={themeFormState.coverBackgroundColor || '#0a0a14'}
                                                            onChange={(e) => handleFieldChange('coverBackgroundColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Texto</label>
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color me-2"
                                                            value={themeFormState.coverTextColor || '#ffffff'}
                                                            onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase"
                                                            value={themeFormState.coverTextColor || '#ffffff'}
                                                            onChange={(e) => handleFieldChange('coverTextColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Control de Oscuridad (Darkness) con Slider e Input */}
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <label className="form-label fw-bold small text-dark mb-0">
                                                        Nivel de Oscurecimiento de Portada (Darkness)
                                                    </label>
                                                    <div className="input-group input-group-sm" style={{ width: '85px' }}>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm text-end fw-bold"
                                                            min="0"
                                                            max="100"
                                                            value={themeFormState.darkness !== undefined ? themeFormState.darkness : 45}
                                                            onChange={(e) => handleFieldChange('darkness', Math.min(100, Math.max(0, Number(e.target.value))))}
                                                        />
                                                        <span className="input-group-text bg-dark text-white fw-bold">%</span>
                                                    </div>
                                                </div>
                                                <input
                                                    type="range"
                                                    className="form-range"
                                                    min="0"
                                                    max="100"
                                                    step="1"
                                                    value={themeFormState.darkness !== undefined ? themeFormState.darkness : 45}
                                                    onChange={(e) => handleFieldChange('darkness', Number(e.target.value))}
                                                />
                                                <div className="d-flex justify-content-between text-muted small">
                                                    <span>0% (Sin sombra)</span>
                                                    <span>45% (Recomendado)</span>
                                                    <span>100% (Negro total)</span>
                                                </div>
                                            </div>

                                            {themeFormState.coverBackgroundType !== 'color' && (
                                                <DragAndDropField
                                                    fieldLabel={`Archivo de Fondo de Portada (${themeFormState.coverBackgroundType === 'video' ? 'Video MP4' : 'Imagen 1920x1080'})`}
                                                    targetFieldName="coverBackgroundUrl"
                                                    mediaCategory="background"
                                                    currentValue={themeFormState.coverBackgroundUrl}
                                                    onUploadFile={handleSingleFileUpload}
                                                    resolveMediaUrl={resolveMediaUrl}
                                                    isProcessing={activeUploadFieldKey === 'coverBackgroundUrl'}
                                                    onClear={handleClearField}
                                                    acceptTypes={themeFormState.coverBackgroundType === 'video' ? 'video/mp4' : 'image/*'}
                                                    helpText="Máx 5MB imágenes, 25MB videos"
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* TAB 2: ESCENARIO ESTADIO */}
                                    {activeTabKey === 'stage' && (
                                        <div className="animate__animated animate__fadeIn">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-tv me-1"></i> Configuración Visual para Pantalla de Estadio / HUD
                                            </h6>

                                            <div className="mb-3">
                                                <label className="form-label fw-bold small text-dark">Tipografía de la Dinámica</label>
                                                <select
                                                    className="form-select"
                                                    value={themeFormState.fontFamily || 'Bangers'}
                                                    onChange={(e) => handleFieldChange('fontFamily', e.target.value)}
                                                >
                                                    {AVAILABLE_FONTS.map((font) => (
                                                        <option key={font.value} value={font.value}>
                                                            {font.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <small className="text-muted">Afecta títulos, marcadores de progreso y textos en pantalla gigante y móviles.</small>
                                            </div>

                                            <div className="row g-3 mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo Estadio</label>
                                                    <select
                                                        className="form-select"
                                                        value={themeFormState.gameBackgroundType || 'image'}
                                                        onChange={(e) => handleFieldChange('gameBackgroundType', e.target.value)}
                                                    >
                                                        <option value="image">Imagen estática</option>
                                                        <option value="video">Video animado (MP4)</option>
                                                        <option value="color">Color sólido</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Fondo</label>
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color me-2"
                                                            value={themeFormState.gameBackgroundColor || '#121224'}
                                                            onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase"
                                                            value={themeFormState.gameBackgroundColor || '#121224'}
                                                            onChange={(e) => handleFieldChange('gameBackgroundColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Texto</label>
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color me-2"
                                                            value={themeFormState.gameTextColor || '#ffffff'}
                                                            onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase"
                                                            value={themeFormState.gameTextColor || '#ffffff'}
                                                            onChange={(e) => handleFieldChange('gameTextColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {themeFormState.gameBackgroundType !== 'color' && (
                                                <DragAndDropField
                                                    fieldLabel={`Fondo de Pantalla de Juego (${themeFormState.gameBackgroundType === 'video' ? 'Video MP4' : 'Imagen 1920x1080'})`}
                                                    targetFieldName="gameBackgroundUrl"
                                                    mediaCategory="background"
                                                    currentValue={themeFormState.gameBackgroundUrl}
                                                    onUploadFile={handleSingleFileUpload}
                                                    resolveMediaUrl={resolveMediaUrl}
                                                    isProcessing={activeUploadFieldKey === 'gameBackgroundUrl'}
                                                    onClear={handleClearField}
                                                    acceptTypes={themeFormState.gameBackgroundType === 'video' ? 'video/mp4' : 'image/*'}
                                                    helpText="Resolución recomendada: 1920x1080 px"
                                                />
                                            )}
                                        </div>
                                    )}

                                    {/* TAB 3: CAPA DE RASPADO Y PREMIO */}
                                    {activeTabKey === 'scratchLayer' && (
                                        <div className="animate__animated animate__fadeIn">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-eraser-fill me-1"></i> Mecánica de Raspado y Recompensas
                                            </h6>

                                            <div className="row g-3 mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold small text-dark">Color de Cobertura de Raspado</label>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color me-2"
                                                            value={themeFormState.scratchCoverColor || '#C0C0C0'}
                                                            onChange={(e) => handleFieldChange('scratchCoverColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase"
                                                            value={themeFormState.scratchCoverColor || '#C0C0C0'}
                                                            onChange={(e) => handleFieldChange('scratchCoverColor', e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary py-0 px-2 small"
                                                            onClick={() => handleFieldChange('scratchCoverColor', '#C0C0C0')}
                                                        >
                                                            Plata (#C0C0C0)
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-warning py-0 px-2 small"
                                                            onClick={() => handleFieldChange('scratchCoverColor', '#FFD700')}
                                                        >
                                                            Oro (#FFD700)
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-dark py-0 px-2 small"
                                                            onClick={() => handleFieldChange('scratchCoverColor', '#222233')}
                                                        >
                                                            Oscuro (#222233)
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold small text-dark d-flex justify-content-between">
                                                        <span>Grosor de Raspado / Moneda</span>
                                                        <span className="badge bg-primary fs-6">{themeFormState.coinSize || 25} px</span>
                                                    </label>
                                                    <input
                                                        type="range"
                                                        className="form-range"
                                                        min="5"
                                                        max="50"
                                                        step="1"
                                                        value={themeFormState.coinSize || 25}
                                                        onChange={(e) => handleFieldChange('coinSize', Number(e.target.value))}
                                                    />
                                                    <div className="d-flex justify-content-between text-muted small">
                                                        <span>5px (Fino)</span>
                                                        <span>25px (Estándar)</span>
                                                        <span>50px (Grueso)</span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <DragAndDropField
                                                        fieldLabel="Textura Superior de Cobertura (Opcional)"
                                                        targetFieldName="scratchCoverImageUrl"
                                                        mediaCategory="cover"
                                                        currentValue={themeFormState.scratchCoverImageUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'scratchCoverImageUrl'}
                                                        onClear={handleClearField}
                                                        helpText="Si se omite, se usa el color sólido plateado/dorado"
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <DragAndDropField
                                                        fieldLabel="Imagen Oculta de Premio / Fondo Revelado *"
                                                        targetFieldName="scratchHiddenImageUrl"
                                                        mediaCategory="hidden"
                                                        currentValue={themeFormState.scratchHiddenImageUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'scratchHiddenImageUrl'}
                                                        onClear={handleClearField}
                                                        helpText="Imagen debajo que el aficionado descubrirá al raspar"
                                                    />
                                                </div>
                                            </div>

                                            <div className="p-3 bg-light rounded-3 border mt-3">
                                                <div className="form-check form-switch mb-2">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="switchShowScratchTool"
                                                        checked={!!themeFormState.showScratchTool}
                                                        onChange={(e) => handleFieldChange('showScratchTool', e.target.checked)}
                                                    />
                                                    <label className="form-check-label fw-bold text-dark" htmlFor="switchShowScratchTool">
                                                        Renderizar cursor visual de moneda / rasqueta en el celular
                                                    </label>
                                                </div>
                                                <small className="text-muted d-block mb-3">
                                                    Si está activado, muestra la imagen de una moneda o rasqueta siguiendo las pulsaciones del aficionado.
                                                </small>

                                                {themeFormState.showScratchTool && (
                                                    <DragAndDropField
                                                        fieldLabel="Icono Personalizado de Moneda / Rasqueta"
                                                        targetFieldName="scratchToolImageUrl"
                                                        mediaCategory="tool"
                                                        currentValue={themeFormState.scratchToolImageUrl}
                                                        onUploadFile={handleSingleFileUpload}
                                                        resolveMediaUrl={resolveMediaUrl}
                                                        isProcessing={activeUploadFieldKey === 'scratchToolImageUrl'}
                                                        onClear={handleClearField}
                                                        helpText="PNG transparente 200x200 px"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB 4: JUGADORES & CELULAR */}
                                    {activeTabKey === 'players' && (
                                        <div className="animate__animated animate__fadeIn">
                                            <h6 className="fw-bold text-primary mb-3">
                                                <i className="bi bi-people-fill me-1"></i> Configuración de Bandos (J1, J2, N Jugadores) y Vista Móvil
                                            </h6>

                                            {/* JUGADOR 1 */}
                                            <div className="p-3 rounded-3 border mb-3" style={{ borderLeft: `6px solid ${themeFormState.player1Color || '#00ffcc'}` }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="badge bg-dark fw-bold">JUGADOR 1 (J1)</span>
                                                    <div className="d-flex align-items-center">
                                                        <label className="form-label small fw-bold mb-0 me-2 text-dark">Color Bando:</label>
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color"
                                                            value={themeFormState.player1Color || '#00ffcc'}
                                                            onChange={(e) => handleFieldChange('player1Color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <label className="form-label small fw-bold text-dark">Nombre / Etiqueta</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Jugador 1"
                                                        value={themeFormState.player1Name || ''}
                                                        onChange={(e) => handleFieldChange('player1Name', e.target.value)}
                                                    />
                                                </div>
                                                <div className="row g-2">
                                                    <div className="col-md-6">
                                                        <DragAndDropField
                                                            fieldLabel="Avatar de Juego J1"
                                                            targetFieldName="player1Avatar"
                                                            mediaCategory="avatar"
                                                            currentValue={themeFormState.player1Avatar}
                                                            onUploadFile={handleSingleFileUpload}
                                                            resolveMediaUrl={resolveMediaUrl}
                                                            isProcessing={activeUploadFieldKey === 'player1Avatar'}
                                                            onClear={handleClearField}
                                                            helpText="PNG transparente 500x500"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <DragAndDropField
                                                            fieldLabel="Avatar de Victoria J1"
                                                            targetFieldName="player1Winner"
                                                            mediaCategory="winner"
                                                            currentValue={themeFormState.player1Winner}
                                                            onUploadFile={handleSingleFileUpload}
                                                            resolveMediaUrl={resolveMediaUrl}
                                                            isProcessing={activeUploadFieldKey === 'player1Winner'}
                                                            onClear={handleClearField}
                                                            helpText="PNG festejo / trofeo"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* JUGADOR 2 */}
                                            <div className="p-3 rounded-3 border mb-3" style={{ borderLeft: `6px solid ${themeFormState.player2Color || '#ff0055'}` }}>
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="badge bg-dark fw-bold">JUGADOR 2 (J2)</span>
                                                    <div className="d-flex align-items-center">
                                                        <label className="form-label small fw-bold mb-0 me-2 text-dark">Color Bando:</label>
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color"
                                                            value={themeFormState.player2Color || '#ff0055'}
                                                            onChange={(e) => handleFieldChange('player2Color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="mb-2">
                                                    <label className="form-label small fw-bold text-dark">Nombre / Etiqueta</label>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Jugador 2"
                                                        value={themeFormState.player2Name || ''}
                                                        onChange={(e) => handleFieldChange('player2Name', e.target.value)}
                                                    />
                                                </div>
                                                <div className="row g-2">
                                                    <div className="col-md-6">
                                                        <DragAndDropField
                                                            fieldLabel="Avatar de Juego J2"
                                                            targetFieldName="player2Avatar"
                                                            mediaCategory="avatar"
                                                            currentValue={themeFormState.player2Avatar}
                                                            onUploadFile={handleSingleFileUpload}
                                                            resolveMediaUrl={resolveMediaUrl}
                                                            isProcessing={activeUploadFieldKey === 'player2Avatar'}
                                                            onClear={handleClearField}
                                                            helpText="PNG transparente 500x500"
                                                        />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <DragAndDropField
                                                            fieldLabel="Avatar de Victoria J2"
                                                            targetFieldName="player2Winner"
                                                            mediaCategory="winner"
                                                            currentValue={themeFormState.player2Winner}
                                                            onUploadFile={handleSingleFileUpload}
                                                            resolveMediaUrl={resolveMediaUrl}
                                                            isProcessing={activeUploadFieldKey === 'player2Winner'}
                                                            onClear={handleClearField}
                                                            helpText="PNG festejo / trofeo"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* N EXTRA PLAYERS */}
                                            {themeFormState.extraPlayers.length > 0 && (
                                                <div className="mb-3">
                                                    <h6 className="fw-bold text-dark small mb-2">Jugadores Adicionales ({themeFormState.extraPlayers.length})</h6>
                                                    {themeFormState.extraPlayers.map((playerItem, playerIndex) => (
                                                        <div key={playerIndex} className="p-3 bg-light rounded-3 border mb-2 position-relative">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <span className="badge bg-secondary">JUGADOR {playerIndex + 3} ({playerItem.id})</span>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger py-0 px-2"
                                                                    onClick={() => handleRemoveExtraPlayer(playerIndex)}
                                                                >
                                                                    <i className="bi bi-trash me-1"></i>Eliminar
                                                                </button>
                                                            </div>
                                                            <div className="row g-2">
                                                                <div className="col-md-6">
                                                                    <label className="form-label small fw-bold text-dark">Nombre</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        value={playerItem.name}
                                                                        onChange={(e) => handleUpdateExtraPlayer(playerIndex, 'name', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className="col-md-6">
                                                                    <label className="form-label small fw-bold text-dark">Color</label>
                                                                    <input
                                                                        type="color"
                                                                        className="form-control form-control-color w-100"
                                                                        value={playerItem.color || '#ffaa00'}
                                                                        onChange={(e) => handleUpdateExtraPlayer(playerIndex, 'color', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary fw-bold w-100 mb-3"
                                                onClick={handleAddExtraPlayer}
                                            >
                                                <i className="bi bi-plus-circle me-1"></i> Agregar Jugador Adicional (J3, J4, ...)
                                            </button>

                                            {/* VISTA MOVIL AFICIONADO */}
                                            <hr className="my-3" />
                                            <h6 className="fw-bold text-secondary mb-2">Fondo de Interfaz Móvil Aficionado</h6>

                                            <div className="row g-3 mb-2">
                                                <div className="col-md-6">
                                                    <label className="form-label fw-bold small text-dark">Tipo de Fondo Móvil</label>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={themeFormState.fanBackgroundType || 'color'}
                                                        onChange={(e) => handleFieldChange('fanBackgroundType', e.target.value)}
                                                    >
                                                        <option value="color">Color Sólido</option>
                                                        <option value="image">Imagen de Fondo</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Fondo</label>
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color w-100"
                                                        value={themeFormState.fanBackgroundColor || '#0d0e1a'}
                                                        onChange={(e) => handleFieldChange('fanBackgroundColor', e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label fw-bold small text-dark">Color Texto</label>
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color w-100"
                                                        value={themeFormState.fanTextColor || '#ffffff'}
                                                        onChange={(e) => handleFieldChange('fanTextColor', e.target.value)}
                                                    />
                                                </div>
                                            </div>

                                            {themeFormState.fanBackgroundType === 'image' && (
                                                <DragAndDropField
                                                    fieldLabel="Imagen de Fondo Móvil"
                                                    targetFieldName="fanBackgroundUrl"
                                                    mediaCategory="background"
                                                    currentValue={themeFormState.fanBackgroundUrl}
                                                    onUploadFile={handleSingleFileUpload}
                                                    resolveMediaUrl={resolveMediaUrl}
                                                    isProcessing={activeUploadFieldKey === 'fanBackgroundUrl'}
                                                    onClear={handleClearField}
                                                    helpText="Formato vertical recomendado (1080x1920)"
                                                />
                                            )}
                                        </div>
                                    )}

                                </form>
                            </div>

                            {/* Right Column: Live Interactive Preview */}
                            <div className="col-lg-5">
                                <div className="card border-0 bg-dark text-white rounded-4 shadow-sm p-3 sticky-top" style={{ top: '10px' }}>
                                    
                                    {/* Preview Switcher */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="fw-bold small text-warning">
                                            <i className="bi bi-eye-fill me-1"></i> Previsualización en Vivo
                                        </span>
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${previewModeKey === 'cover' ? 'btn-warning text-dark fw-bold' : 'btn-outline-secondary text-white'}`}
                                                onClick={() => setPreviewModeKey('cover')}
                                            >
                                                Portada
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${previewModeKey === 'stadium' ? 'btn-warning text-dark fw-bold' : 'btn-outline-secondary text-white'}`}
                                                onClick={() => setPreviewModeKey('stadium')}
                                            >
                                                Estadio
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${previewModeKey === 'scratchCard' ? 'btn-warning text-dark fw-bold' : 'btn-outline-secondary text-white'}`}
                                                onClick={() => setPreviewModeKey('scratchCard')}
                                            >
                                                Raspado
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${previewModeKey === 'mobile' ? 'btn-warning text-dark fw-bold' : 'btn-outline-secondary text-white'}`}
                                                onClick={() => setPreviewModeKey('mobile')}
                                            >
                                                Móvil
                                            </button>
                                        </div>
                                    </div>

                                    {/* PREVIEW CONTAINER */}
                                    <div
                                        className="rounded-3 overflow-hidden position-relative border border-secondary shadow-inner d-flex flex-column justify-content-center align-items-center text-center p-3"
                                        style={{
                                            minHeight: '380px',
                                            maxHeight: '450px',
                                            backgroundColor:
                                                previewModeKey === 'cover'
                                                    ? themeFormState.coverBackgroundColor || '#0a0a14'
                                                    : previewModeKey === 'stadium'
                                                    ? themeFormState.gameBackgroundColor || '#121224'
                                                    : previewModeKey === 'mobile'
                                                    ? themeFormState.fanBackgroundColor || '#0d0e1a'
                                                    : '#151525',
                                            backgroundImage:
                                                previewModeKey === 'cover' && themeFormState.coverBackgroundUrl
                                                    ? `url(${resolveMediaUrl(themeFormState.coverBackgroundUrl)})`
                                                    : previewModeKey === 'stadium' && themeFormState.gameBackgroundUrl
                                                    ? `url(${resolveMediaUrl(themeFormState.gameBackgroundUrl)})`
                                                    : previewModeKey === 'mobile' && themeFormState.fanBackgroundUrl
                                                    ? `url(${resolveMediaUrl(themeFormState.fanBackgroundUrl)})`
                                                    : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            fontFamily: themeFormState.fontFamily || 'Bangers'
                                        }}
                                    >
                                        {/* Overlay shading for readability */}
                                        <div
                                            className="position-absolute top-0 start-0 w-100 h-100"
                                            style={{
                                                backgroundColor: `rgba(0, 0, 0, ${(themeFormState.darkness !== undefined ? themeFormState.darkness : 45) / 100})`,
                                                zIndex: 1
                                            }}
                                        ></div>

                                        {/* PREVIEW 1: PORTADA */}
                                        {previewModeKey === 'cover' && (
                                            <div className="position-relative w-100 p-3" style={{ zIndex: 2 }}>
                                                {/* Logos Header */}
                                                {((themeFormState.showBrandLogo !== false && themeFormState.brandLogoUrl) ||
                                                    (themeFormState.showSponsorLogo !== false && themeFormState.sponsorLogoUrl)) && (
                                                    <div className="d-flex justify-content-center align-items-center gap-3 mb-2">
                                                        {themeFormState.showBrandLogo !== false && themeFormState.brandLogoUrl && (
                                                            <img
                                                                src={resolveMediaUrl(themeFormState.brandLogoUrl)}
                                                                alt="Marca"
                                                                style={{ maxHeight: '45px', maxWidth: '90px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        {themeFormState.showSponsorLogo !== false && themeFormState.sponsorLogoUrl && (
                                                            <img
                                                                src={resolveMediaUrl(themeFormState.sponsorLogoUrl)}
                                                                alt="Sponsor"
                                                                style={{ maxHeight: '40px', maxWidth: '80px', objectFit: 'contain' }}
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                {/* Presents Text */}
                                                {themeFormState.showPresentsText !== false && (
                                                    <p
                                                        className="small fw-bold text-uppercase mb-1 tracking-wider"
                                                        style={{ color: themeFormState.coverTextColor || '#ffffff', opacity: 0.9, letterSpacing: '3px' }}
                                                    >
                                                        {themeFormState.coverPresentsText || 'PRESENTA'}
                                                    </p>
                                                )}

                                                {/* Cover Title */}
                                                {themeFormState.showCoverTitle !== false && (
                                                    <h2
                                                        className="fw-bold text-uppercase mb-2 text-shadow"
                                                        style={{ color: themeFormState.coverTextColor || '#ffffff', letterSpacing: '2px' }}
                                                    >
                                                        {themeFormState.coverTitle || 'RASCA Y GANA'}
                                                    </h2>
                                                )}

                                                <div className="badge bg-warning text-dark px-3 py-2 fs-6 fw-bold text-uppercase mt-2 shadow">
                                                    ¡PREPÁRATE PARA RASCAR Y GANAR!
                                                </div>
                                            </div>
                                        )}

                                        {/* PREVIEW 2: ESCENARIO ESTADIO */}
                                        {previewModeKey === 'stadium' && (
                                            <div className="position-relative w-100 p-2" style={{ zIndex: 2 }}>
                                                <h4 className="fw-bold text-warning mb-3">DUELO DE RASPADO EN VIVO</h4>
                                                
                                                <div className="row g-2 justify-content-center">
                                                    {/* J1 Mini Card */}
                                                    <div className="col-5">
                                                        <div className="p-2 rounded-3 border bg-dark bg-opacity-75">
                                                            {themeFormState.player1Avatar ? (
                                                                <img
                                                                    src={resolveMediaUrl(themeFormState.player1Avatar)}
                                                                    alt="J1"
                                                                    style={{ height: '40px', objectFit: 'contain' }}
                                                                />
                                                            ) : (
                                                                <i className="bi bi-person-fill fs-2" style={{ color: themeFormState.player1Color }}></i>
                                                            )}
                                                            <div className="fw-bold small text-truncate" style={{ color: themeFormState.player1Color }}>
                                                                {themeFormState.player1Name || 'J1'}
                                                            </div>
                                                            <div className="progress mt-1" style={{ height: '10px' }}>
                                                                <div
                                                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                                                    style={{ width: '65%', backgroundColor: themeFormState.player1Color }}
                                                                ></div>
                                                            </div>
                                                            <small className="fw-bold text-white">65%</small>
                                                        </div>
                                                    </div>

                                                    <div className="col-2 d-flex align-items-center justify-content-center">
                                                        <span className="fw-bold fs-4 text-warning">VS</span>
                                                    </div>

                                                    {/* J2 Mini Card */}
                                                    <div className="col-5">
                                                        <div className="p-2 rounded-3 border bg-dark bg-opacity-75">
                                                            {themeFormState.player2Avatar ? (
                                                                <img
                                                                    src={resolveMediaUrl(themeFormState.player2Avatar)}
                                                                    alt="J2"
                                                                    style={{ height: '40px', objectFit: 'contain' }}
                                                                />
                                                            ) : (
                                                                <i className="bi bi-person-fill fs-2" style={{ color: themeFormState.player2Color }}></i>
                                                            )}
                                                            <div className="fw-bold small text-truncate" style={{ color: themeFormState.player2Color }}>
                                                                {themeFormState.player2Name || 'J2'}
                                                            </div>
                                                            <div className="progress mt-1" style={{ height: '10px' }}>
                                                                <div
                                                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                                                    style={{ width: '40%', backgroundColor: themeFormState.player2Color }}
                                                                ></div>
                                                            </div>
                                                            <small className="fw-bold text-white">40%</small>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-3 badge bg-danger fs-6 px-3 py-1">
                                                    ⏱ TIEMPO: {themeFormState.maxTimeSeconds || 20}s | META: {themeFormState.targetPercentage || 100}%
                                                </div>
                                            </div>
                                        )}

                                        {/* PREVIEW 3: TARJETA DE RASPADO */}
                                        {previewModeKey === 'scratchCard' && (
                                            <div className="position-relative w-100 p-2" style={{ zIndex: 2 }}>
                                                <h6 className="fw-bold text-warning mb-2">Simulación de Tarjeta de Raspado</h6>
                                                
                                                <div
                                                    className="mx-auto rounded-3 border border-3 border-warning shadow position-relative overflow-hidden"
                                                    style={{
                                                        width: '200px',
                                                        height: '200px',
                                                        backgroundColor: themeFormState.scratchCoverColor || '#C0C0C0',
                                                        backgroundImage: themeFormState.scratchCoverImageUrl
                                                            ? `url(${resolveMediaUrl(themeFormState.scratchCoverImageUrl)})`
                                                            : 'none',
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center'
                                                    }}
                                                >
                                                    {/* Simulated Scratched Hole with Hidden Image behind */}
                                                    <div
                                                        className="position-absolute rounded-circle shadow-inner overflow-hidden border border-2 border-white"
                                                        style={{
                                                            width: '100px',
                                                            height: '100px',
                                                            top: '50px',
                                                            left: '50px',
                                                            backgroundColor: '#ffffff',
                                                            backgroundImage: themeFormState.scratchHiddenImageUrl
                                                                ? `url(${resolveMediaUrl(themeFormState.scratchHiddenImageUrl)})`
                                                                : 'none',
                                                            backgroundSize: 'cover',
                                                            backgroundPosition: 'center'
                                                        }}
                                                    >
                                                        {!themeFormState.scratchHiddenImageUrl && (
                                                            <div className="d-flex h-100 align-items-center justify-content-center text-dark fw-bold small">
                                                                ¡PREMIO!
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Simulated Coin Cursor */}
                                                    <div
                                                        className="position-absolute rounded-circle bg-warning border border-2 border-dark shadow"
                                                        style={{
                                                            width: `${themeFormState.coinSize || 25}px`,
                                                            height: `${themeFormState.coinSize || 25}px`,
                                                            bottom: '20px',
                                                            right: '20px',
                                                            transform: 'translate(50%, 50%)'
                                                        }}
                                                        title={`Moneda (${themeFormState.coinSize || 25}px)`}
                                                    ></div>
                                                </div>

                                                <small className="text-secondary d-block mt-2">
                                                    Grosor actual: <strong>{themeFormState.coinSize || 25}px</strong> | Cobertura: <strong>{themeFormState.scratchCoverColor}</strong>
                                                </small>
                                            </div>
                                        )}

                                        {/* PREVIEW 4: VISTA MÓVIL AFICIONADO */}
                                        {previewModeKey === 'mobile' && (
                                            <div className="position-relative p-2" style={{ zIndex: 2, width: '190px' }}>
                                                <div className="border border-3 border-secondary rounded-4 p-2 bg-black shadow-lg">
                                                    <div className="badge bg-danger w-100 mb-1 py-1" style={{ fontSize: '9px' }}>
                                                        🔴 EN VIVO
                                                    </div>
                                                    <div
                                                        className="rounded-2 p-2 mb-2 text-center"
                                                        style={{
                                                            backgroundColor: themeFormState.scratchCoverColor || '#C0C0C0',
                                                            minHeight: '110px'
                                                        }}
                                                    >
                                                        <span className="badge bg-dark" style={{ fontSize: '8px' }}>¡RASCA AQUÍ!</span>
                                                    </div>
                                                    <div className="progress mb-1" style={{ height: '6px' }}>
                                                        <div className="progress-bar bg-success" style={{ width: '50%' }}></div>
                                                    </div>
                                                    <div className="d-flex justify-content-between text-white" style={{ fontSize: '9px' }}>
                                                        <span>50%</span>
                                                        <span>Meta: {themeFormState.targetPercentage}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                    {/* Preview Metadata */}
                                    <div className="mt-3 small text-secondary">
                                        <div><strong className="text-white">Tema:</strong> {themeFormState.name || '(Sin nombre)'}</div>
                                        <div><strong className="text-white">Tipografía:</strong> {themeFormState.fontFamily || 'Bangers'}</div>
                                        <div><strong className="text-white">Duración:</strong> {themeFormState.maxTimeSeconds || 20}s | <strong className="text-white">Meta:</strong> {themeFormState.targetPercentage || 100}%</div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Footer */}
                    <div className="modal-footer bg-light border-0 py-3 px-4 d-flex justify-content-between">
                        <button
                            type="button"
                            className="btn btn-secondary px-4 fw-bold"
                            onClick={onCloseModal}
                            disabled={isSavingInProgress}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary px-5 fw-bold shadow"
                            onClick={handleSaveThemeSubmit}
                            disabled={isSavingInProgress}
                        >
                            {isSavingInProgress ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check2-circle me-1"></i> Guardar Tema
                                </>
                            )}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};
